import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import apiService from 'v2/services/api';

import type { TransferHistoryEntry } from 'v2/interfaces/financial';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { ApiService } from 'v2/services/api';

export const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 15,
};

class TransferHistory {
  private loading$ = new Rx.BehaviorSubject<boolean>(true);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private transferHistory$ = new Rx.BehaviorSubject<TransferHistoryEntry[]>(null);

  constructor(private apiServices: ApiService) {}

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;
  public getTransferHistory = (): Rx.Observable<TransferHistoryEntry[]> => this.transferHistory$;

  public isLoading = (): Rx.Observable<boolean> => this.loading$;

  public setCompositionRequest = (transferId: number, email: string) => {
    return this.apiServices.post(`transfers/${transferId}/composition`, { destination: email });
  };

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ current_page: 1, per_page: 15 });
    return this.getPaginationParams();
  };

  public loadData() {
    return this.resetPage().pipe(
      map((paginationParams) => ({
        current_page: paginationParams.current_page,
        per_page: paginationParams.per_page,
      })),
      debounceTime(10),
      switchMap((pagination) => {
        this.loading$.next(true);
        return this.apiServices.get('transfers/history', pagination).pipe(finalize(() => this.loading$.next(false)));
      }),
      tap((response) => this.transferHistory$.next(response.data)),
      tap((response) => this.paginationResponse$.next(response.pagination))
    );
  }
}

export const transferHistoryService = new TransferHistory(apiService);
